import { Tooltip } from "devextreme-react/tooltip";
import { format } from "date-fns";
import React, { useId } from "react";
import "./FileLink.css";

// What every file link needs. FileDto has all of it; the Task Summary's comment files carry the
// same fields.
export type FileLinkInfo = {
    id: string;
    name: string;
    uploadDate?: Date | string | null;
    fileSize?: number | null;
    ownerName?: string | null;
    user?: { fullName?: string | null } | null;
};

type Props = {
    file: FileLinkInfo;
    onClick: () => void;
    // Off for template attachments, which have no real upload date, size or owner.
    showInfo?: boolean;
    testId?: string;
};

// Human-readable file size; mirrors the legacy file manager's size column.
export const formatFileSize = (bytes?: number | null) => {
    if (bytes == null) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null;
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? null : d;
};

export const fileOwner = (file: FileLinkInfo) => file.ownerName ?? file.user?.fullName ?? "";

// Column headings for a list of FileLinks: the same row layout with labels in place of values, so
// the headings line up with the columns and the Owner heading comes and goes with the owner column.
export function FileListHeader({ nameLabel = "Name" }: { nameLabel?: string }) {
    return (
        <span className="file-link-box file-link-header" data-testid="file-list-header">
            <span className="file-link">
                <span className="file-link-name">{nameLabel}</span>
                <span className="file-link-date">Date</span>
                <span className="file-link-owner">Owner</span>
            </span>
        </span>
    );
}

// The one way a file shows up anywhere in the tasklist: its name as a download link, the upload
// date beside it, the owner too when the list is wide enough for a third column (see the CSS), and
// a hover tooltip with the name, modified date, size and owner. The same link everywhere so no file
// list has to grow columns of its own.
export function FileLink({ file, onClick, showInfo = true, testId = "file-link-name" }: Props) {
    // useId's colons are not valid in a CSS id selector.
    const targetId = `file-link-${useId().replace(/[:]/g, "")}`;
    const modified = toDate(file.uploadDate);
    const owner = fileOwner(file);

    return (
        <span className="file-link-box">
            <span className="file-link">
                <span id={targetId} data-testid={testId} className="file-link-name" onClick={onClick}>
                    {file.name}
                </span>
                {showInfo && modified && (
                    <span data-testid="file-link-date" className="file-link-date">{format(modified, "MM/dd/yyyy")}</span>
                )}
                {showInfo && owner && (
                    <span data-testid="file-link-owner" className="file-link-owner">{owner}</span>
                )}
            </span>
            {/* Outside the row: the tooltip's own wrapper element takes up space in a flex row
                while it is shown, and shoved the date along. */}
            {showInfo && (
                <Tooltip
                    target={`#${targetId}`}
                    showEvent="mouseenter"
                    hideEvent="mouseleave"
                    hideOnOutsideClick={false}
                    position="top"
                >
                    <div data-testid="file-link-tooltip" className="file-link-tooltip">
                        <div className="file-link-tooltip-name">{file.name}</div>
                        {modified && <div>Modified: {format(modified, "MM/dd/yyyy h:mm a")}</div>}
                        {file.fileSize != null && <div>Size: {formatFileSize(file.fileSize)}</div>}
                        {owner && <div>Owner: {owner}</div>}
                    </div>
                </Tooltip>
            )}
        </span>
    );
}
